<template>
	<div class="modal-container ModalAddEditPayment">
		<div class="modal-header">
			<span>Payment Details</span>

			<EditInfo
				v-if="!isNewPayment"
				:editableObject="payment"
			/>
		</div>
		<div class="modal-body">
			<fieldset
				:disabled="modal.isModalDisabled"
				class="no-margin-grid"
				:style="$grid('1')"
			>
				<Panel
					v-if="customer"
					title="Basic Info"
				>
					<div
						:style="$grid('1-1-1-1')"
						class="no-margin-grid"
					></div>
					<div
						:style="$grid('1-1-1')"
						class="no-margin-grid"
					>
						<InputRichDropdown
							v-model:saturn="payment.type"
							:list="modal.paymentMethods"
							:valueMap="(pf: ToListItems<PaymentMethods>) => pf.value"
							:display="(pf: ToListItems<PaymentMethods>) => pf.display"
							:invalid="v$.payment.type.$invalid"
							:disabled="hasPaperworkPacket"
							nothingSelected="-- PAYMENT Type --"
						>
						</InputRichDropdown>
						<InputCurrency
							label="Amount"
							placeholder="Amount"
							v-model:saturn="payment.amount"
							:precision="2"
							:invalid="v$.payment.amount.$invalid"
							:disabled="hasPaperworkPacket"
						>
							<template
								#validation
								v-if="v$.payment.amount.$silentErrors.length > 0"
							>
								<div
									v-for="error in v$.payment.amount.$silentErrors"
									:key="error"
								>
									{{ error.$message }}
								</div>
							</template>
						</InputCurrency>
						<InputTextbox
							label="Receipt #"
							v-model:saturn="payment.receiptNumber"
							placeholder="Receipt number"
							:isUpperCase="true"
							:invalid="v$.payment.receiptNumber.$invalid"
							:disabled="modal.isModalDisabled"
						>
							<template #validation>
								<div v-if="v$.payment.receiptNumber.$invalid">Receipt # is required</div>
							</template>
						</InputTextbox>
					</div>
				</Panel>

				<Panel title="Customer Info">
					<template #HeaderComponent>
						<button
							@click="handleRetrieveCustomer"
							:disabled="hasPaperworkPacket"
						>
							Retrieve Customer
						</button>
					</template>

					<div v-if="!customer">Please first retrieve a customer</div>
					<div
						v-else
						:style="$grid('1')"
						class="no-margin-grid"
					>
						<InputTextbox
							label="Full name"
							v-model:saturn="displayName"
							:isUpperCase="true"
							:disabled="true"
						>
						</InputTextbox>

						<div
							:style="$grid('1-1-1')"
							class="no-margin-grid"
						>
							<InputDate
								label="Date of Birth"
								v-model:saturn="customer.dob"
								:disabled="true"
								icon="fas fa-calendar"
							>
							</InputDate>

							<InputTextbox
								label="Email"
								v-model:saturn="customer.email"
								:isUpperCase="true"
								:disabled="true"
							>
							</InputTextbox>

							<InputPhone
								label="Cell Phone"
								type="tel"
								v-model:saturn="customer.cell"
								placeholder="Cell Phone"
								:disabled="true"
								maxlength="10"
								minlength="10"
							>
							</InputPhone>
						</div>
						<fieldset :disabled="true">
							<AddressComponent
								:data="customer"
								:showCounty="true"
							>
							</AddressComponent>
						</fieldset>
					</div>
				</Panel>
			</fieldset>
		</div>
		<div class="modal-footer modal-footer-split">
			<div>
				<button
					v-if="!isNewPayment"
					class="button-unwind"
					@click="deletePayment()"
					:disabled="payment.amount && hasPaperworkPacket"
				>
					Delete Payment
				</button>
			</div>
			<div>
				<button
					class="button-unwind"
					@click="cancel()"
				>
					Cancel
				</button>
				<button
					class="button-save"
					@click="close()"
					:disabled="modal.isModalDisabled || v$.$invalid"
				>
					Save
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	export interface IDownPaymentBucketModalProps extends IBucketModalProps {
		fimenu: FIMenu;
		paymentMethods: ToListItems<PaymentMethods>[];
	}
</script>

<script setup lang="ts">
	import { computed, ref } from 'vue';
	import $modal from '@core/services/modal';
	import AddressComponent from '@/components/AddressComponent.vue';
	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import EditInfo from '@/components/EditInfo.vue';
	import ENUMS from '@core/classes/Enums';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuBuyersOrder from '@core/classes/FIMenuBuyersOrder';
	import FIMenuBuyersOrderBucket from '@core/classes/FIMenuBuyersOrderBucket';
	import FIMenuBuyersOrderBucketItem from '@core/classes/FIMenuBuyersOrderBucketItem';
	import FIMenuCustomer from '@core/classes/FIMenuCustomer';
	import FIMenuDownPayment from '@core/classes/FIMenuDownPayment';
	import { IBucketModalProps } from '@core/_interfaces/ComponentProps';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import InputDate from '@core/components/InputDate.vue';
	import InputPhone from '@core/components/InputPhone.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import { openRetrieveCustomersModal } from '@core/helpers/Customer/customer-helper';
	import Panel from '@core/components/Panel.vue';
	import { PaymentMethods } from '@core/classes/SharedEnums';
	import { ToListItems } from '@core/helpers/enum-helper';
	import { useVuelidate } from '@vuelidate/core';
	import util from '@core/services/util';

	const { modal } = defineProps<{
		modal: IDownPaymentBucketModalProps;
	}>();

	const user = auth.getTokenPayload();

	const DOWN_PAYMENT_TITLE_ID = '10013';

	// Copy Buyer's Order
	const bo = ref<FIMenuBuyersOrder>(new FIMenuBuyersOrder(modal.bo));
	const downPayments = ref(modal.fimenu.downPayments.map(dp => new FIMenuDownPayment(dp)));
	const payment = ref<FIMenuDownPayment>(null);
	const customer = ref<FIMenuCustomer>(null);
	const bucket = ref<FIMenuBuyersOrderBucket>(modal.bucketContent.parentBucket);
	const isNewPayment = ref(false);

	const displayName = computed(() => {
		return customer.value?.getPaperworkName()?.toUpperCase() ?? '';
	});
	// const customerAddress = computed(() => {
	//     return customer.value?.getFullAddress();
	// });
	const hasPaperworkPacket = computed(() => {
		return modal.fimenu.hasPaperworkPacket();
	});

	//IF NEW PAYMENT, CREATE IT AND CREATE A BUCKET FOR IT
	if (!modal.bucketContent.bucketItem) {
		isNewPayment.value = true;

		//CREATE NEW PAYMENT AND NEW BUCKET
		const foundBucket = bo.value.buckets.find(b => b.bucketType == 'Cash Down' && b.titleId == DOWN_PAYMENT_TITLE_ID);
		if (foundBucket) {
			bucket.value = foundBucket;
		} else {
			bucket.value = bo.value.addNewDefaultBucket(modal.bucketContent.parentBucket.bucketType, 0, 0, null, null, DOWN_PAYMENT_TITLE_ID);
		}

		//CREATE NEW PAYMENT AND NEW BUCKET
		payment.value = getNewDefaultDownPayment();
	} else {
		isNewPayment.value = false;

		// Find corresponding bucket in Buyer's Order copy
		bucket.value = bo.value.buckets.find(
			b =>
				b.bucketType === modal.bucketContent.parentBucket.bucketType &&
				b.originalTitleId === modal.bucketContent.parentBucket.originalTitleId &&
				b.titleId === modal.bucketContent.parentBucket.titleId &&
				b.taxIndex === modal.bucketContent.parentBucket.taxIndex &&
				b.upfrontTaxIndex === modal.bucketContent.parentBucket.upfrontTaxIndex,
		);

		payment.value = downPayments.value.find(downPayment => downPayment.id == modal.bucketContent.bucketItem.metadata?.paymentId);

		customer.value = new FIMenuCustomer(modal.fimenu.customersInfo.customers.find(c => c.id == payment.value.customerId));

		if (!bucket.value.titleId) {
			bucket.value = bo.value.addNewDefaultBucket(bucket.value.bucketType, 0, 0, null, DOWN_PAYMENT_TITLE_ID, DOWN_PAYMENT_TITLE_ID);
		}
	}

	async function fetchCustomer(customer: FIMenuCustomer) {
		try {
			const customerResponse = await api.customers.getCustomerById(customer.id);
			if (!customerResponse?.data) util.toastr('error', 'Error', "Coulnd't find customer.");
			return customerResponse.data;
		} catch (err) {
			util.toastr('error', 'Error', 'An error has ocurred while retrieving the customer.');
			$modal.cancel();
		}
	}
	function handleRetrieveCustomer() {
		const actionRetrieveCustomer = async (newCustomer: FIMenuCustomer) => {
			payment.value.customerId = newCustomer.id;
			const customerData = await fetchCustomer(newCustomer);
			customer.value = new FIMenuCustomer();
			customer.value.setFromCustomer(customerData);
		};

		openRetrieveCustomersModal({
			actionOnRetrieveCustomer: actionRetrieveCustomer,
			actionOnRetrieveCompany: actionRetrieveCustomer,
			canRetrieveCompany: true,
		});
	}
	function getNewDefaultDownPayment() {
		const downPayment = new FIMenuDownPayment();
		downPayment.id = util.getGuid();
		return downPayment;
	}
	function deletePayment() {
		const result = confirm('Are you sure you want to delete this payment?');
		if (result) {
			modal.fimenu.downPayments.splice(
				modal.fimenu.downPayments.findIndex(dp => dp.id === payment.value.id),
				1,
			);
			bucket.value.items = bucket.value.items.filter((i: FIMenuBuyersOrderBucketItem) => i.metadata?.paymentId !== payment.value.id);
			// Remove customer from customersInfo
			modal.fimenu.cleanUpCustomers();
			$modal.close(bo.value);
		}
	}
	function close() {
		let item = bucket.value.items.find((i: FIMenuBuyersOrderBucketItem) => i.metadata?.paymentId === payment.value.id);
		if (item) {
			modal.fimenu.downPayments.splice(
				modal.fimenu.downPayments.findIndex(dp => dp.id === payment.value.id),
				1,
				payment.value,
			);
		} else {
			item = new FIMenuBuyersOrderBucketItem({
				bucketItem: {
					profitFlag: ENUMS.PROFIT_FLAGS.NONE,
					name: modal.paymentMethods.find(o => o.value == payment.value.type).display,
					sign: -1,
					metadata: {
						paymentId: payment.value.id,
					},
				},
				parentBucket: bucket.value,
			});

			modal.fimenu.downPayments.push(payment.value);
			bucket.value.items.push(item);
		}

		item.amount = payment.value.amount;
		item.profit = null;

		// Update last edit info
		payment.value.editedById = user.EmployeeId;
		payment.value.editedByName = user.EmployeeName;
		payment.value.lastModifiedAt = new Date();

		if (isNewPayment.value) {
			payment.value.createdById = user.EmployeeId;
			payment.value.createdByName = user.EmployeeName;
			payment.value.createdAt = new Date(payment.value.lastModifiedAt);
		}

		//update customers list
		modal.fimenu.upsertCustomer(customer.value);

		$modal.close(bo.value);
	}
	function cancel() {
		$modal.cancel();
	}

	const validations = computed(() => ({
		payment: {
			...payment.value.validation(modal.fimenu),
			isDownPaymentTotalValid: true,
		} as any,
	}));

	const v$ = useVuelidate(validations, { payment });
</script>
<style>
	.ModalAddEditRebate .description {
		justify-content: flex-start;
	}
</style>
